import type { ReactElement } from 'react'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'

/**
 * Tailwind's preflight strips list markers, heading sizes and link styling from
 * generated markup, so restore them here with arbitrary-variant utilities. The
 * base text color is left to inherit so the content blends with whatever wraps
 * it (a light card on the Arena, the themed public page). Kept as a plain string
 * — not run through `cn()` — so `tailwind-merge` never touches these selectors.
 */
const PROSE = [
  'text-sm leading-relaxed break-words',
  '[&>*:first-child]:mt-0 [&>*:last-child]:mb-0',
  '[&_p]:my-2',
  '[&_h1]:mt-3 [&_h1]:mb-1 [&_h1]:text-lg [&_h1]:font-semibold',
  '[&_h2]:mt-3 [&_h2]:mb-1 [&_h2]:text-lg [&_h2]:font-semibold',
  '[&_h3]:mt-3 [&_h3]:mb-1 [&_h3]:text-base [&_h3]:font-semibold',
  '[&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5',
  '[&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5',
  '[&_li]:my-0.5',
  '[&_a]:text-blue-600 [&_a]:underline',
  '[&_strong]:font-semibold [&_em]:italic',
  '[&_blockquote]:my-2 [&_blockquote]:border-l-[3px] [&_blockquote]:border-gray-200 [&_blockquote]:pl-3'
].join(' ')

export interface MarkdownContentProps {
  /** Markdown source. */
  children: string
  /** Extra classes appended to the prose wrapper (e.g. spacing, text color). */
  className?: string
}

/**
 * Renders markdown safely — `react-markdown` does not emit raw HTML by default,
 * so untrusted content is displayed without an HTML injection surface. Used for
 * tournament event descriptions on both the Arena (read-back) and the public
 * follow-along.
 */
export function MarkdownContent({ children, className }: MarkdownContentProps): ReactElement {
  return (
    <div className={className ? `${PROSE} ${className}` : PROSE}>
      <ReactMarkdown remarkPlugins={[remarkGfm]}>{children}</ReactMarkdown>
    </div>
  )
}
